import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl } from '../../../utils/mediaUtils';
import { 
  Compass, 
  Upload, 
  Clock, 
  Save, 
  Eye, 
  X 
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra
  };
};

const fieldLabels = {
  kicker: 'Section Kicker',
  title: 'Main Title',
  summary: 'Summary / Description',
  ctaLabel: 'CTA Button Label',
  ctaHref: 'CTA Destination URL',
  image: 'Cover Photograph'
};

export default function FacilitiesTourForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Virtual Tour',
    title: 'Virtual Tour Experience',
    summary: 'Explore classrooms, labs, and campus facilities with a guided virtual tour from anywhere.',
    ctaLabel: 'Start Virtual Tour',
    ctaHref: '#',
    image: ''
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    summary: false,
    ctaLabel: false,
    ctaHref: false,
    image: false
  });
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [brokenImage, setBrokenImage] = useState(false);

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache', ...getAuthHeaders() }
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.ok && Array.isArray(json.data)) {
        const map = {};
        json.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[FacilitiesTourForm] Media fetch error:', err.message);
    }
  };

  const resolveImage = (mediaRef) => {
    if (!mediaRef || brokenImage) return '';
    if (typeof mediaRef === 'object' && mediaRef !== null) {
      mediaRef = mediaRef.image || mediaRef.url || mediaRef.file_path || '';
    }
    const str = String(mediaRef).trim();
    if (!str) return '';
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (mediaMap && (mediaMap[str] || mediaMap[String(str)])) {
      const mapped = mediaMap[str] || mediaMap[String(str)];
      if (mapped.startsWith('http://') || mapped.startsWith('https://')) return mapped;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${mapped}` : mapped;
    }
    const resolved = getImageUrl(str, mediaMap);
    if (resolved && resolved !== str) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://')) return resolved;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${resolved}` : resolved;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }
    return '';
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=facilities`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'facilities-tour' || s.section_type === 'virtual-tour' || s.section_type === 'tour');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=facilities`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'facilities-tour' || s.section_type === 'virtual-tour' || s.section_type === 'tour');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const loadedData = {
          kicker: en.kicker || 'Virtual Tour',
          title: en.title || 'Virtual Tour Experience',
          summary: en.summary || en.description || 'Explore classrooms, labs, and campus facilities with a guided virtual tour from anywhere.',
          ctaLabel: en.ctaLabel || en.cta_label || 'Start Virtual Tour',
          ctaHref: en.ctaHref || en.cta_href || '#',
          image: en.image || ''
        };

        setFormData(loadedData);
        setOriginalData(loadedData);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, summary: false, ctaLabel: false, ctaHref: false, image: false };

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  const fid = c.field_id;
                  if (pFields.hasOwnProperty(fid)) {
                    pFields[fid] = true;
                    if (c.new_value !== undefined && c.new_value !== null) {
                      loadedData[fid] = c.new_value;
                    }
                  } else if (fid === 'description' && pFields.summary) {
                    pFields.summary = true;
                    loadedData.summary = c.new_value;
                  }
                }
              });

              setPendingFields(pFields);
              setFormData(loadedData);
            }
          }
        } catch (pe) {
          console.warn('[FacilitiesTourForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[FacilitiesTourForm] Load error:', err);
      toast.error('Gagal memuat konten Virtual Tour');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran file foto maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Hanya format JPG, PNG, dan WebP yang didukung');
      return;
    }

    setImageUploading(true);
    setUploadProgress(0);

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('section_type', 'facilities-tour');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(pct);
      }
    };

    xhr.onload = () => {
      setImageUploading(false);
      setUploadProgress(0);
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          const returnedUrl = data.url || data.file_path || data.path || res.url || (data.filename ? `/api/uploads/images/${data.filename}` : '');
          const origin = (apiBase || '').replace(/\/api\/?$/, '');
          const fullUrl = returnedUrl.startsWith('http') ? returnedUrl : (returnedUrl ? `${origin}${returnedUrl}` : '');
          const finalUrl = fullUrl || returnedUrl || String(mediaId || '');

          if (finalUrl) {
            handleFieldChange('image', finalUrl);
            setBrokenImage(false);
            if (mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: finalUrl,
                [String(mediaId)]: finalUrl
              }));
            }
            toast.success('Foto virtual tour berhasil diupload');
          } else {
            toast.error('Gagal membaca URL gambar dari server');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(false);
      setUploadProgress(0);
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (field) => {
    if (!sectionId) {
      toast.error('Section ID belum dimuat');
      return;
    }

    if (field === 'image' && (!formData.image || brokenImage)) {
      toast.warning('Silakan upload foto terlebih dahulu sebelum submit');
      return;
    }

    if (formData[field] === originalData[field]) {
      toast.warning('Tidak ada perubahan pada kolom ini');
      return;
    }

    setSubmitting(prev => ({ ...prev, [field]: true }));
    const label = fieldLabels[field] || (field.charAt(0).toUpperCase() + field.slice(1));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: label,
          old_value: originalData[field] || '',
          new_value: formData[field],
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${label}" berhasil dikirim untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: formData[field] }));
      } else {
        toast.error(result.error || result.message || 'Gagal mengirim pengajuan');
      }
    } catch (err) {
      console.error('[FacilitiesTourForm] Submit error:', err);
      toast.error('Error saat submit: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Memuat bagian Virtual Tour...</p>
      </div>
    );
  }

  const tourImageUrl = resolveImage(formData.image);

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img 
              src={lightboxImage} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '12px', objectFit: 'contain' }}
            />
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
          </div>
        </div>
      )}

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Compass size={22} />
        </div>
        <div>
          <h2 className="unified-title">Virtual Tour Experience</h2>
          <p className="unified-subtitle">
            Konfigurasi banner ajakan tur virtual 360 derajat, tautan tujuan, dan foto sampul
          </p>
        </div>
      </div>

      {/* Field: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Virtual Tour"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Menyimpan...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Virtual Tour Experience"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Menyimpan...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Summary */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Summary / Description</label>
          {pendingFields.summary && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.summary}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            disabled={pendingFields.summary || submitting.summary}
            placeholder="Penjelasan singkat tentang tur virtual..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('summary')}
            disabled={pendingFields.summary || submitting.summary || formData.summary === originalData.summary}
          >
            <Save size={14} /> {submitting.summary ? 'Menyimpan...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: CTA Label & Link */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Call to Action (CTA) Button</label>
            <p className="unified-field-desc">
              Teks tombol dan tautan tujuan untuk membuka pengalaman tur virtual
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '12px' }}>
          <div>
            <div className="unified-field-header" style={{ marginBottom: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>CTA Button Label</label>
              {pendingFields.ctaLabel && (
                <span className="unified-pending-pill">
                  <Clock size={11} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                style={{ fontSize: '13px' }}
                value={formData.ctaLabel}
                onChange={(e) => handleFieldChange('ctaLabel', e.target.value)}
                disabled={pendingFields.ctaLabel || submitting.ctaLabel}
                placeholder="e.g. Start Virtual Tour"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('ctaLabel')}
                disabled={pendingFields.ctaLabel || submitting.ctaLabel || formData.ctaLabel === originalData.ctaLabel}
              >
                <Save size={13} /> Save
              </button>
            </div>
          </div>

          <div>
            <div className="unified-field-header" style={{ marginBottom: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>CTA Destination URL</label>
              {pendingFields.ctaHref && (
                <span className="unified-pending-pill">
                  <Clock size={11} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                style={{ fontSize: '13px' }}
                value={formData.ctaHref}
                onChange={(e) => handleFieldChange('ctaHref', e.target.value)}
                disabled={pendingFields.ctaHref || submitting.ctaHref}
                placeholder="e.g. https://tour.example.com or #"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('ctaHref')}
                disabled={pendingFields.ctaHref || submitting.ctaHref || formData.ctaHref === originalData.ctaHref}
              >
                <Save size={13} /> Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Field: Tour Cover Image */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Virtual Tour Cover Photograph</label>
          {pendingFields.image && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', marginTop: '10px', flexWrap: 'wrap' }}>
          {tourImageUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div 
                style={{ 
                  width: '180px', 
                  height: '110px', 
                  borderRadius: '10px', 
                  overflow: 'hidden', 
                  background: '#0f172a',
                  position: 'relative',
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                }}
              >
                <img 
                  src={tourImageUrl} 
                  alt="Tour preview" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => setLightboxImage(tourImageUrl)}
                  onError={() => setBrokenImage(true)}
                />
                <button
                  type="button"
                  onClick={() => setLightboxImage(tourImageUrl)}
                  style={{
                    position: 'absolute',
                    bottom: '6px',
                    right: '6px',
                    background: 'rgba(0,0,0,0.65)',
                    border: 'none',
                    borderRadius: '4px',
                    color: '#fff',
                    padding: '3px 6px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title="Lihat Foto"
                >
                  <Eye size={12} />
                </button>
              </div>
              <label 
                className="unified-secondary-btn"
                style={{ 
                  cursor: pendingFields.image || imageUploading ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  padding: '8px 14px'
                }}
              >
                <Upload size={14} />
                {imageUploading ? `Mengupload (${uploadProgress}%)...` : 'Ganti Foto'}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  disabled={pendingFields.image || imageUploading}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          ) : (
            <label 
              style={{
                width: '180px',
                height: '110px',
                borderRadius: '10px',
                border: '2px dashed #cbd5e1',
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: pendingFields.image || imageUploading ? 'not-allowed' : 'pointer',
                gap: '6px',
                padding: '10px',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ background: '#f1f5f9', borderRadius: '50%', padding: '8px', display: 'flex' }}>
                <Upload size={18} color="#64748b" />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                {imageUploading ? `Mengupload (${uploadProgress}%)...` : 'Upload Cover Foto'}
              </span>
              {imageUploading && (
                <div style={{ width: '80%', height: '5px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#7c3aed', transition: 'width 0.2s ease' }} />
                </div>
              )}
              <span style={{ fontSize: '10px', color: '#94a3b8' }}>JPG, PNG, WebP (Maks 5MB)</span>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload}
                disabled={pendingFields.image || imageUploading}
                style={{ display: 'none' }}
              />
            </label>
          )}

          <div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('image')}
              disabled={pendingFields.image || submitting.image || formData.image === originalData.image || !formData.image || brokenImage}
            >
              <Save size={14} /> {submitting.image ? 'Menyimpan...' : 'Save & Submit Cover'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
